import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

const categoryNames: Record<string, string> = {
  all: '全部漫画',
  romance: '恋爱',
  fantasy: '奇幻',
  action: '动作',
  thriller: '悬疑',
  comedy: '搞笑',
  drama: '剧情',
  horror: '恐怖',
  daily: '日常',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const catSlug = slug || 'all';
  const catName = categoryNames[catSlug] || '全部漫画';
  const catInfo = categories.find((c) => c.slug === catSlug);

  const filteredComics = catSlug === 'all'
    ? comics
    : comics.filter((c) => c.categories.includes(catSlug));

  const pageTitle = catSlug === 'all'
    ? '韩漫分类-免费漫画大全-全部韩国漫画在线阅读'
    : `${catName}韩漫-免费${catName}漫画大全-好看的${catName}韩国漫画推荐`;

  const pageDesc = catSlug === 'all'
    ? '韩国漫画网分类页，收录全部韩国漫画。恋爱、奇幻、动作、悬疑、搞笑等各类韩漫免费在线阅读，无遮挡下拉式阅读舒适体验。'
    : `韩国漫画网${catName}分类，提供最全免费${catName}韩国漫画。精选${catName}题材韩漫，无遮挡下拉式在线阅读，流畅舒适体验。`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`韩国漫画,韩漫,${catName}漫画,免费漫画,漫画大全,在线阅读`}
        canonicalUrl={`http://sweetbird.cn/category/${catSlug}`}
      />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100">
          <div className="w-full px-4 md:px-6 py-12">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '漫画分类', path: '/category/all' },
                { label: catSlug === 'all' ? '全部漫画' : catName },
              ]}
            />

            <div className="mt-4">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900">
                {catSlug === 'all' ? '全部漫画' : `${catName}漫画`}
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-2xl">
                {catSlug === 'all'
                  ? '浏览全部韩国漫画作品，涵盖恋爱、奇幻、动作、悬疑等多种题材，无遮挡下拉式阅读，给你最好的在线阅读体验。'
                  : `精选${catName}题材韩国漫画，${catInfo ? `共收录${catInfo.count}部作品` : ''}。下拉式流畅阅读，沉浸式漫画体验。`}
              </p>
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
              <Link
                to="/category/all"
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  catSlug === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-300/60 hover:border-primary-300'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    catSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-300/60 hover:border-primary-300'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                  <i className="ri-book-open-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}